/**
 * formCascade - PR-E2c-cascade.
 *
 * Cascade engine for form-level catches written from the sub-dex grid.
 * Mirrors the existing base-species cascade design in `dexStateStore.ts`
 * (`ensureMainCaughtFromCascade`, `CUSTOM_DEX_LINKAGE`) but operates at
 * the (variant, dexNumber, formId) granularity introduced by
 * PR-E2c-store.
 *
 * Two cascade axes fire for every form catch:
 *
 *   1. FORM FANOUT across variants per `CASCADE_GRAPH`.
 *      Catching Shundo Vivillon-Icy-Snow ON cascades the SAME form to
 *      Shiny / Perfect / Pokemon. Catching Shiny Furfrou-Pharaoh ON
 *      cascades only to Pokemon. Each target row is keyed
 *      `${target}:${dexNumber}:${formId}`.
 *
 *   2. PARENT SPECIES FLAG ("at least one form caught" -> base species
 *      counts as caught). For every (target variant, dexNumber) touched
 *      by the form fanout above (plus the source row itself), ensure
 *      the BASE-species row `${variant}:${dexNumber}` is also marked
 *      present. This honours the spec rule: "if any form of a species
 *      is caught, the species is caught."
 *
 * Both axes are strictly ONE-DIRECTIONAL and never destructive:
 *
 *   - Form catch in source variant => upgrades target variant forms +
 *     base species rows (form fanout + parent flag).
 *   - Form REMOVAL (state="missing") does NOT propagate. Removing a
 *     single form catch does not imply the user lost Lucky/Shiny/Main
 *     copies of that form, and certainly does not un-catch the species.
 *     This is symmetric with the base-species cascade in dexStateStore.
 *   - A row already present is NEVER downgraded.
 *   - A row marked "missing" IS upgraded to "present" by a cascade,
 *     REGARDLESS of source -- the user said "I have this" via the source
 *     form, and under last-write-wins (2026-07-18) that fresh catch is the
 *     newest statement, so it trumps a prior "I don't have it" even when the
 *     prior was a manual edit. This matches the base-species cascade
 *     `writeMainPresentIfAbsent` (`ensureMainCaughtFromCascade`), whose manual
 *     guard was removed by the same commit so the two cascades stay identical.
 *     The old "refuse to overwrite a manual MISSING" exception was exactly the
 *     source-precedence rule that made a cleared dex unscannable, so it is gone.
 *
 * Server sync: the cascade is write-through. Each cell that gets
 * upgraded is enqueued for the existing debounced PUT pump via
 * `enqueuePending`, so the server's `dex_records` table receives the
 * cascaded rows. The server's own `composeUserDexState` is unchanged --
 * it will see the same set of rows that the client wrote and produce
 * matching counts.
 */

import type { CellState, PokedexVariant } from "@/lib/pogoPokedexVariantClassifier";
import type { DexEntryState } from "@/lib/dexStateStore";

/**
 * CASCADE_GRAPH - declarative downstream targets for each variant.
 *
 * Sourced from the locked variant-axis brief; combines the read-side
 * cascade rule (`CASCADING_VARIANTS` in dexStateStore.ts: shiny / lucky
 * / xxl / xxs / perfect imply MAIN) with the write-side custom-dex
 * linkage (`CUSTOM_DEX_LINKAGE`: shundo -> shiny + perfect + main,
 * shinyLucky -> shiny + lucky + main).
 *
 * Variants with `[]` are terminal - no cascade fans out. mega / gmax /
 * shadow / purified are intentionally terminal because they describe a
 * temporary form, not an underlying ownership of the base species.
 */
export const CASCADE_GRAPH: Record<PokedexVariant, readonly PokedexVariant[]> = {
  pokemon: [],
  shiny: ["pokemon"],
  lucky: ["pokemon"],
  perfect: ["pokemon"],
  xxl: ["pokemon"],
  xxs: ["pokemon"],
  mega: [],
  gmax: [],
  shadow: [],
  purified: [],
  // pokedex-complete-dex: derived, never written — no cascade in or out.
  complete: [],
  shundo: ["shiny", "perfect", "pokemon"],
  shinyLucky: ["shiny", "lucky", "pokemon"],
  // dmax-dynamax-dex: a Dynamax catch is a real catch of the base species
  // (you keep it in storage), so it cascades to MAIN like the custom dexes.
  dynamax: ["pokemon"],
  // form-dexes: a regional form and a costumed copy are both real catches of
  // the base species (an Alolan Meowth IS a Meowth), so both cascade to MAIN
  // like the custom dexes. Terminal otherwise — neither says anything about
  // whether the species is also shiny, lucky or perfect.
  regional: ["pokemon"],
  costume: ["pokemon"],
};

/**
 * Sentinel structure describing a single cascaded write. The dexStateStore
 * caller persists these to the keyed store and enqueues each for the
 * server sync pump. Kept as plain data (no DOM/storage side-effects)
 * so the engine is unit-testable in isolation.
 */
export interface CascadeWrite {
  /** Storage key (`${variant}:${dexNumber}` or with `:${formId}`). */
  key: string;
  /** Full entry to write at that key. */
  entry: DexEntryState;
  /** Marker: is this a base-species write (parent flag) or a form fanout. */
  kind: "form-fanout" | "parent-species";
}

/** Internal helper -- keep in sync with dexStateStore.keyFor. */
function keyFor(
  variant: PokedexVariant,
  dexNumber: number,
  formId?: string | null,
): string {
  return formId ? `${variant}:${dexNumber}:${formId}` : `${variant}:${dexNumber}`;
}

/**
 * Decide whether an upgrade is allowed at a given key. Returns:
 *   - true  -> safe to write `present` (no prior, or any prior that is not
 *              already "present" -- including a manual "missing").
 *   - false -> prior is already `present` (no-op upgrade -- skip enqueue).
 *
 * LAST-WRITE-WINS (2026-07-18): a fresh catch cascading onto a cell is the
 * newest, truest statement about it, so it overwrites a prior manual "not
 * caught" -- catching the species via a form trumps an older "I don't have
 * it". This deliberately matches `writeMainPresentIfAbsent`
 * (`ensureMainCaughtFromCascade`) in dexStateStore, which the same commit
 * stripped of its manual guard so the base-species cascade and the form
 * cascade stay identical. The only thing we never touch is an already-present
 * row (nothing to upgrade, and we avoid churning its updatedAt).
 */
function canUpgradeToPresent(prev: DexEntryState | undefined): boolean {
  if (!prev) return true;
  if (prev.state === "present") return false; // already counted -- no-op
  // prev.state is "missing" or "unknown", of any source: a fresh catch is the
  // most recent statement and wins (last-write-wins).
  return true;
}

/**
 * Compute the full set of writes triggered by toggling a form catch on
 * in `(sourceVariant, dexNumber, formId)`. Pure function: reads from the
 * caller-supplied `readEntry` lookup, never mutates storage. Returns the
 * writes in deterministic order (form fanout first, then parent flags)
 * so the caller can apply them and replay in tests.
 *
 * Only fires when `state === "present"`. Removing a form catch is NOT
 * cascaded - see the file header for the rationale.
 */
export function computeFormCascade(args: {
  sourceVariant: PokedexVariant;
  dexNumber: number;
  formId: string;
  state: CellState;
  /** Lookup hook so the engine can stay pure. */
  readEntry: (
    variant: PokedexVariant,
    dexNumber: number,
    formId?: string | null,
  ) => DexEntryState | undefined;
  /** Timestamp for written rows. Injectable for deterministic tests. */
  now?: () => number;
}): CascadeWrite[] {
  const { sourceVariant, dexNumber, formId, state, readEntry } = args;
  const now = args.now ?? Date.now;
  const writes: CascadeWrite[] = [];

  if (state !== "present") return writes;
  if (!Number.isFinite(dexNumber) || dexNumber < 1) return writes;
  if (!formId) return writes;

  const ts = now();
  const targets = CASCADE_GRAPH[sourceVariant] ?? [];

  // 1. FORM FANOUT. Write the SAME formId to each downstream variant.
  for (const target of targets) {
    const prev = readEntry(target, dexNumber, formId);
    if (!canUpgradeToPresent(prev)) continue;
    writes.push({
      key: keyFor(target, dexNumber, formId),
      entry: {
        variant: target,
        dexNumber,
        state: "present",
        source: "scan",
        updatedAt: ts,
        formId,
        // Preserve any manualBaseline that existed on the prior row so a
        // future revert keeps working. We never invent a baseline here.
        ...(prev?.manualBaseline != null ? { manualBaseline: prev.manualBaseline } : {}),
      },
      kind: "form-fanout",
    });
  }

  // 2. PARENT SPECIES FLAG. For the SOURCE variant and each FANOUT
  //    target variant, ensure the base-species row (no formId) is
  //    present. This realises the "any form caught -> species caught"
  //    rule.
  const parentVariants: PokedexVariant[] = [sourceVariant, ...targets];
  for (const v of parentVariants) {
    const prev = readEntry(v, dexNumber, null);
    if (!canUpgradeToPresent(prev)) continue;
    writes.push({
      key: keyFor(v, dexNumber),
      entry: {
        variant: v,
        dexNumber,
        state: "present",
        source: "scan",
        updatedAt: ts,
        ...(prev?.manualBaseline != null ? { manualBaseline: prev.manualBaseline } : {}),
      },
      kind: "parent-species",
    });
  }

  return writes;
}
